import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Globe, Box, Save, Check, Search, CircleMinus, CircleCheck } from 'lucide-react';
import Skeleton from '../../common/Skeleton';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmWarning, confirmActive } from '../../common/confirm';
import { ProcedureTemplate, PhysicalCheckTemplate, physicalCheckTemplateService } from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';
import organizationLocationsService from '../../../services/organizationLocationsService';
import MultiSelectChips, { MultiSelectOption } from '../../common/MultiSelectChips';
import NoDataFound from '../../common/NoDataFound';

interface ProcedurePhysicalInspectionProps {
    procedure: ProcedureTemplate;
}

const ASSET_TYPE_OPTIONS: MultiSelectOption[] = [
    { value: 'Computers', label: 'Computers' },
    { value: 'Servers', label: 'Servers' },
    { value: 'Buildings', label: 'Buildings' },
    { value: 'Inventories', label: 'Inventories' },
    { value: 'Stores', label: 'Stores' },
    { value: 'Spares', label: 'Spares' },
    { value: 'Consumables', label: 'Consumables' },
    { value: 'Cash', label: 'Cash' },
    { value: 'PPE', label: 'PPE (Plant, Property, Equipment)' },
    { value: 'Vehicles', label: 'Vehicles' },
    { value: 'Furniture', label: 'Furniture & Fixtures' },
];

const COVERAGE_OPTIONS = ['All', 'Sample', 'Threshold'];
const COVERAGE_SELECT_OPTIONS = COVERAGE_OPTIONS.map(c => ({ value: c, label: c }));

const csvSplit = (s?: string | null): string[] =>
    !s ? [] : s.split(',').map(p => p.trim()).filter(Boolean);

interface FormState {
    asset_types: string[];
    location_ids: string[];
    coverage_basis: string;
    coverage_threshold: string;
    notes: string;
}

const blank = (): FormState => ({ asset_types: [], location_ids: [], coverage_basis: 'All', coverage_threshold: '', notes: '' });

const ProcedurePhysicalInspection: React.FC<ProcedurePhysicalInspectionProps> = ({ procedure }) => {
    const [items, setItems] = useState<PhysicalCheckTemplate[]>([]);
    const [locations, setLocations] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<PhysicalCheckTemplate | null>(null);
    const [formData, setFormData] = useState<FormState>(blank);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const locationOptions = useMemo<MultiSelectOption[]>(
        () => locations.map(l => ({ value: String(l.id), label: l.name })),
        [locations]
    );
    const locationLabel = useCallback((id: string) => locationOptions.find(o => o.value === id)?.label || `#${id}`, [locationOptions]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, locs] = await Promise.all([
                physicalCheckTemplateService.listForProcedure(procedure.id),
                organizationLocationsService.getDropdown().catch(() => [] as { id: number; name: string }[]),
            ]);
            setItems(list);
            setLocations(locs);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load inspection rows.') });
        } finally {
            setLoading(false);
        }
    }, [procedure.id]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    useEffect(() => {
        if (isModalOpen) {
            setSubmitting(false);
            setToggling(false);
        }
    }, [isModalOpen, currentItem]);

    const handleAdd = useCallback(() => {
        setCurrentItem(null);
        setFormData(blank());
        setModalOpen(true);
    }, []);

    const handleEdit = (item: PhysicalCheckTemplate) => {
        setCurrentItem(item);
        setFormData({
            asset_types: csvSplit(item.asset_type),
            location_ids: csvSplit(item.geography_scope),
            coverage_basis: item.coverage_basis || 'All',
            coverage_threshold: item.coverage_threshold || '',
            notes: item.notes || '',
        });
        setModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!currentItem) return;
        const isActive = (currentItem.is_active ?? 1) === 1;
        const next = isActive ? 0 : 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this inspection row?' : 'Inactivate this inspection row?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await physicalCheckTemplateService.update(currentItem.id, { is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (formData.asset_types.length === 0 && formData.location_ids.length === 0) {
            setToast({ type: 'warning', message: 'Pick at least one asset type or one location.' });
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                asset_type: formData.asset_types.join(',') || null,
                geography_scope: formData.location_ids.join(',') || null,
                coverage_basis: formData.coverage_basis || null,
                coverage_threshold: formData.coverage_threshold || null,
                notes: formData.notes || null,
            };
            if (currentItem) {
                await physicalCheckTemplateService.update(currentItem.id, payload);
                setToast({ type: 'success', message: 'Inspection updated.' });
            } else {
                await physicalCheckTemplateService.create({ procedure_template_id: procedure.id, is_active: 1, ...payload });
                setToast({ type: 'success', message: 'Inspection created.' });
            }
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Save failed.') });
        } finally {
            setSubmitting(false);
        }
    };

    const isEditMode = !!currentItem;
    const isItemActive = (currentItem?.is_active ?? 1) === 1;
    const editTitle = currentItem
        ? (csvSplit(currentItem.asset_type)[0]
            || locationLabel(csvSplit(currentItem.geography_scope)[0] || '')
            || `Inspection #${currentItem.id}`)
        : '';

    const filtered = items.filter(it => {
        const hay = `${it.asset_type ?? ''} ${it.geography_scope ?? ''} ${it.coverage_basis ?? ''} ${it.coverage_threshold ?? ''} ${it.notes ?? ''}`.toLowerCase();
        return hay.includes(searchTerm.toLowerCase());
    });
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <>
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_physical_inspection_template_search"
                    />
                    <Button variant="fill" icon={Plus} data-testid="BTN_physical_inspection_template_create" onClick={handleAdd}>Create</Button>
                </div>
            </div>
            <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Asset Types</th>
                                <th className="table-list-th">Locations</th>
                                <th className="table-list-th">Coverage</th>
                                <th className="table-list-th">Threshold / Notes</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'pill', 'sm']} />}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={5} className="table-list-cell"><NoDataFound title="No Inspections" message="No inspections defined yet." /></td></tr>
                            )}
                            {!loading && paginatedRows.map((item, idx) => {
                                const assets = csvSplit(item.asset_type);
                                const locs = csvSplit(item.geography_scope);
                                return (
                                    <tr key={item.id} className="table-list-row" data-testid={`BTN_physical_inspection_template_row_${item.id}_open`} onClick={() => handleEdit(item)}>
                                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                        <td className="table-list-cell">
                                            <div className="flex flex-wrap gap-1 items-center">
                                                <Box size={14} className="text-gray-400" />
                                                {assets.length === 0 && <span className="text-gray-400 italic">—</span>}
                                                {assets.map(a => <span key={a} className="status-badge xs info">{a}</span>)}
                                            </div>
                                        </td>
                                        <td className="table-list-cell">
                                            <div className="flex flex-wrap gap-1 items-center">
                                                <Globe size={14} className="text-blue-500" />
                                                {locs.length === 0 && <span className="text-gray-400 italic">—</span>}
                                                {locs.map(id => <span key={id} className="status-badge xs info">{locationLabel(id)}</span>)}
                                            </div>
                                        </td>
                                        <td className="table-list-cell">
                                            <span className={`status-badge xs ${item.coverage_basis === 'All' ? 'success' : item.coverage_basis === 'Sample' ? 'warning' : 'info'}`}>
                                                {item.coverage_basis || '—'}
                                            </span>
                                        </td>
                                        <td className="table-list-cell">
                                            {item.coverage_threshold && <div className="font-medium text-gray-700">{item.coverage_threshold}</div>}
                                            {item.notes && <div className="text-xs text-gray-500 mt-0.5">{item.notes}</div>}
                                            {!item.coverage_threshold && !item.notes && <span className="text-gray-400 italic">—</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    recordLabel="inspections"
                />
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} size="md">
                <Modal.Header>
                    {isEditMode
                        ? <>Editing "<span className="item-highlight">{editTitle}</span>"</>
                        : 'Create Inspection'}
                </Modal.Header>
                <Modal.Body>
                    <form id="physical-inspection-form" data-testid="MOD_physical_inspection_template_form" onSubmit={handleSave} className="flex flex-col gap-y-4">
                        <FormControl.FormGroup label="Asset Types" required>
                            <MultiSelectChips
                                options={ASSET_TYPE_OPTIONS}
                                selected={formData.asset_types}
                                onChange={(vs: string[]) => setFormData(f => ({ ...f, asset_types: vs }))}
                                placeholder="Pick one or more asset types…"
                            />
                        </FormControl.FormGroup>
                        <FormControl.FormGroup label="Locations / Geography">
                            <MultiSelectChips
                                options={locationOptions}
                                selected={formData.location_ids}
                                onChange={(vs: string[]) => setFormData(f => ({ ...f, location_ids: vs }))}
                                placeholder={locationOptions.length === 0 ? 'No locations defined for this entity' : 'Pick one or more locations…'}
                                disabled={locationOptions.length === 0}
                            />
                        </FormControl.FormGroup>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                            <FormControl.SearchSelect
                                label="Coverage Basis"
                                data-testid="SEL_physical_inspection_template_coverage_basis"
                                options={COVERAGE_SELECT_OPTIONS}
                                value={formData.coverage_basis}
                                onChange={v => setFormData({ ...formData, coverage_basis: String(v ?? 'All') })}
                            />
                            <FormControl.Input
                                label={formData.coverage_basis === 'Threshold' ? 'Threshold' : 'Sample basis'}
                                data-testid="INP_physical_inspection_template_coverage_threshold"
                                value={formData.coverage_threshold}
                                onChange={e => setFormData({ ...formData, coverage_threshold: e.target.value })}
                                placeholder={formData.coverage_basis === 'Threshold' ? '> 100k USD value' : 'e.g. 50 random items'}
                            />
                        </div>
                        <FormControl.Textarea
                            label="Notes / Methodology"
                            data-testid="INP_physical_inspection_template_notes"
                            placeholder="Anything specific about how this inspection is performed."
                            value={formData.notes}
                            onChange={e => setFormData({ ...formData, notes: e.target.value })}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
                    {isEditMode && (
                        <Button
                            variant={isItemActive ? 'inactive-outline' : 'active-outline'}
                            icon={isItemActive ? CircleMinus : CircleCheck}
                            data-testid="BTN_physical_inspection_template_toggle_active"
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                        >
                            {toggling ? '...' : isItemActive ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        form="physical-inspection-form"
                        variant={isEditMode ? 'update' : 'fill'}
                        icon={isEditMode ? Check : Save}
                        data-testid="BTN_physical_inspection_template_submit"
                        disabled={submitting || toggling}
                    >
                        {submitting
                            ? isEditMode ? 'Updating...' : 'Submitting...'
                            : isEditMode ? 'Update' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default ProcedurePhysicalInspection;
